import React, { useMemo } from "react";
import { Autocomplete, TextField, Box, Typography } from "@mui/material";
import moment from "moment-timezone";

// Major cities and regions grouped by continent
const TIMEZONE_GROUPS = {
  Popular: [
    { city: "London, United Kingdom", zone: "Europe/London" },
    { city: "New York, United States", zone: "America/New_York" },
    { city: "Los Angeles, United States", zone: "America/Los_Angeles" },
    { city: "Tokyo, Japan", zone: "Asia/Tokyo" },
    { city: "Sydney, Australia", zone: "Australia/Sydney" },
    { city: "Dubai, UAE", zone: "Asia/Dubai" },
    { city: "UTC", zone: "UTC" },
  ],
  "North America": [
    { city: "New York, United States", zone: "America/New_York" },
    { city: "Los Angeles, United States", zone: "America/Los_Angeles" },
    { city: "Chicago, United States", zone: "America/Chicago" },
    { city: "Toronto, Canada", zone: "America/Toronto" },
    { city: "Vancouver, Canada", zone: "America/Vancouver" },
    { city: "Mexico City, Mexico", zone: "America/Mexico_City" },
  ],
  Europe: [
    { city: "London, United Kingdom", zone: "Europe/London" },
    { city: "Paris, France", zone: "Europe/Paris" },
    { city: "Berlin, Germany", zone: "Europe/Berlin" },
    { city: "Madrid, Spain", zone: "Europe/Madrid" },
    { city: "Rome, Italy", zone: "Europe/Rome" },
    { city: "Amsterdam, Netherlands", zone: "Europe/Amsterdam" },
    { city: "Moscow, Russia", zone: "Europe/Moscow" },
  ],
  Asia: [
    { city: "Tokyo, Japan", zone: "Asia/Tokyo" },
    { city: "Shanghai, China", zone: "Asia/Shanghai" },
    { city: "Singapore", zone: "Asia/Singapore" },
    { city: "Dubai, UAE", zone: "Asia/Dubai" },
    { city: "Hong Kong", zone: "Asia/Hong_Kong" },
    { city: "Seoul, South Korea", zone: "Asia/Seoul" },
    { city: "Mumbai, India", zone: "Asia/Kolkata" },
  ],
  Pacific: [
    { city: "Sydney, Australia", zone: "Australia/Sydney" },
    { city: "Melbourne, Australia", zone: "Australia/Melbourne" },
    { city: "Auckland, New Zealand", zone: "Pacific/Auckland" },
    { city: "Brisbane, Australia", zone: "Australia/Brisbane" },
    { city: "Perth, Australia", zone: "Australia/Perth" },
  ],
  "South America": [
    { city: "Sao Paulo, Brazil", zone: "America/Sao_Paulo" },
    { city: "Buenos Aires, Argentina", zone: "America/Argentina/Buenos_Aires" },
    { city: "Santiago, Chile", zone: "America/Santiago" },
    { city: "Lima, Peru", zone: "America/Lima" },
  ],
};

const TimeZoneSelector = ({
  value,
  onChange,
  label,
  helperText,
  disabled,
  error,
  id,
}) => {
  // Create the options list with current time offsets
  const options = useMemo(() => {
    const now = moment();
    const allOptions = [];

    // Add grouped options
    Object.entries(TIMEZONE_GROUPS).forEach(([group, cities]) => {
      cities.forEach(({ city, zone }) => {
        const offset = moment.tz(zone).format("Z");
        const currentTime = now.tz(zone).format("HH:mm");
        allOptions.push({
          group,
          city,
          zone,
          offset,
          currentTime,
          display: `${city} (${currentTime}, GMT${offset})`,
        });
      });
    });

    // Sort options by offset
    return allOptions.sort((a, b) => {
      const offsetA = parseInt(a.offset.replace(":", ""));
      const offsetB = parseInt(b.offset.replace(":", ""));
      return offsetA - offsetB;
    });
  }, []);

  // Find the current option based on the value
  const currentOption = options.find((opt) => opt.zone === value);

  return (
    <Autocomplete
      id={id}
      options={options}
      groupBy={(option) => option.group}
      getOptionLabel={(option) => option.display || ""}
      disabled={disabled}
      value={currentOption || null}
      onChange={(_, newValue) => {
        if (newValue) {
          onChange(newValue.zone);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          error={error}
          fullWidth
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Typography variant="body2">
            {option.city}{" "}
            <Typography
              component="span"
              color="textSecondary"
              variant="caption"
            >
              ({option.currentTime}, GMT{option.offset})
            </Typography>
          </Typography>
        </Box>
      )}
      isOptionEqualToValue={(option, value) => option.zone === value.zone}
    />
  );
};

export default TimeZoneSelector;
